/**
 * Application Status Management Utility
 * 
 * This utility handles all application status transitions, validations, and UI permissions
 * 
 * Available Statuses:
 * - Draft: Initial status when application is created
 * - Submitted: Form completed and signed form uploaded
 * - Missing Information: Admin has requested additional information
 * - Reviewed: Admin has reviewed the application
 * - Awarded: Application has been approved and aid granted
 * - Rejected: Application has been rejected
 * - Pending Signed Form: Form submitted but signed form not uploaded
 * - Re-Upload Signed Form: Admin requested to re-upload signed form
 */

export const APPLICATION_STATUS = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    MISSING_INFORMATION: 'Missing Information',
    REVIEWED: 'Reviewed',
    AWARDED: 'Awarded',
    REJECTED: 'Rejected',
    PENDING_SIGNED_FORM: 'Pending Signed Form',
    RE_UPLOAD_SIGNED_FORM: 'Re-Upload Signed Form'
};

// Status flow definitions
export const STATUS_FLOW = {
    [APPLICATION_STATUS.DRAFT]: [
        APPLICATION_STATUS.PENDING_SIGNED_FORM,
        APPLICATION_STATUS.SUBMITTED
    ],
    [APPLICATION_STATUS.PENDING_SIGNED_FORM]: [
        APPLICATION_STATUS.SUBMITTED,
        APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM
    ],
    [APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM]: [
        APPLICATION_STATUS.SUBMITTED
    ],
    [APPLICATION_STATUS.SUBMITTED]: [
        APPLICATION_STATUS.MISSING_INFORMATION,
        APPLICATION_STATUS.REVIEWED,
        APPLICATION_STATUS.AWARDED,
        APPLICATION_STATUS.REJECTED
    ],
    [APPLICATION_STATUS.MISSING_INFORMATION]: [
        APPLICATION_STATUS.PENDING_SIGNED_FORM,
        APPLICATION_STATUS.SUBMITTED
    ],
    [APPLICATION_STATUS.REVIEWED]: [
        APPLICATION_STATUS.AWARDED,
        APPLICATION_STATUS.REJECTED,
        APPLICATION_STATUS.MISSING_INFORMATION
    ],
    [APPLICATION_STATUS.AWARDED]: [], // Final status
    [APPLICATION_STATUS.REJECTED]: []  // Final status
};

// User permissions for each status
export const STATUS_PERMISSIONS = {
    [APPLICATION_STATUS.DRAFT]: {
        canEdit: true,
        canSaveDraft: true,
        canSubmit: true,
        canView: true,
        canDownloadPDF: false,
        canUploadSignedForm: false,
        canDownloadSignedForm: false,
        showResumeButton: false,
        isReadOnly: false
    },
    [APPLICATION_STATUS.PENDING_SIGNED_FORM]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: true,
        canUploadSignedForm: true,
        canDownloadSignedForm: false,
        showResumeButton: false,
        isReadOnly: true
    },
    [APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: true,
        canUploadSignedForm: true,
        canDownloadSignedForm: false,
        showResumeButton: false,
        isReadOnly: true
    },
    [APPLICATION_STATUS.SUBMITTED]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: true,
        canUploadSignedForm: false,
        canDownloadSignedForm: true,
        showResumeButton: false,
        isReadOnly: true
    },
    [APPLICATION_STATUS.MISSING_INFORMATION]: {
        canEdit: true,
        canSaveDraft: true,
        canSubmit: true,
        canView: true,
        canDownloadPDF: true,
        canUploadSignedForm: false,
        canDownloadSignedForm: true,
        showResumeButton: true,
        isReadOnly: false
    },
    [APPLICATION_STATUS.REVIEWED]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: false,
        canUploadSignedForm: false,
        canDownloadSignedForm: true,
        showResumeButton: false,
        isReadOnly: true
    },
    [APPLICATION_STATUS.AWARDED]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: false,
        canUploadSignedForm: false,
        canDownloadSignedForm: true,
        showResumeButton: false,
        isReadOnly: true
    },
    [APPLICATION_STATUS.REJECTED]: {
        canEdit: false,
        canSaveDraft: false,
        canSubmit: false,
        canView: true,
        canDownloadPDF: false,
        canUploadSignedForm: false,
        canDownloadSignedForm: true,
        showResumeButton: false,
        isReadOnly: true
    }
};

// Status display properties for UI
export const STATUS_DISPLAY = {
    [APPLICATION_STATUS.DRAFT]: {
        badgeClass: 'bg-warning',
        icon: 'fas fa-edit',
        message: 'Application is in draft state'
    },
    [APPLICATION_STATUS.PENDING_SIGNED_FORM]: {
        badgeClass: 'bg-primary',
        icon: 'fas fa-upload',
        message: 'Please upload your signed application form'
    },
    [APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM]: {
        badgeClass: 'bg-warning',
        icon: 'fas fa-upload',
        message: 'Please re-upload your signed application form'
    },
    [APPLICATION_STATUS.SUBMITTED]: {
        badgeClass: 'bg-info',
        icon: 'fas fa-check-circle',
        message: 'Application submitted successfully'
    },
    [APPLICATION_STATUS.MISSING_INFORMATION]: {
        badgeClass: 'bg-danger',
        icon: 'fas fa-exclamation-triangle',
        message: 'Additional information required. Please resume and update your application'
    },
    [APPLICATION_STATUS.REVIEWED]: {
        badgeClass: 'bg-info', // Show as Submitted
        icon: 'fas fa-check-circle',
        message: 'Application submitted successfully'
    },
    [APPLICATION_STATUS.AWARDED]: {
        badgeClass: 'bg-success',
        icon: 'fas fa-trophy',
        message: 'Congratulations! Your application has been awarded'
    },
    [APPLICATION_STATUS.REJECTED]: {
        badgeClass: 'bg-info', // Show as Submitted
        icon: 'fas fa-check-circle',
        message: 'Application submitted successfully'
    }
};
/**
 * TEMPORARY LOGIC: Map status for display in application list
 * REVIEWED and REJECTED should show as SUBMITTED
 * To revert: Remove this function and remove getDisplayStatus from all imports
 * @param {string} status
 * @returns {string}
 */
export const getDisplayStatus = (status) => {
    // --- TEMPORARY STATUS MAPPING START ---
    if (status === APPLICATION_STATUS.REVIEWED || status === APPLICATION_STATUS.REJECTED) {
        return APPLICATION_STATUS.SUBMITTED;
    }
    // --- TEMPORARY STATUS MAPPING END ---
    return status;
};

/**
 * Get permissions for a specific status
 * @param {string} status - The application status
 * @returns {Object} - Permissions object
 */
export const getStatusPermissions = (status) => {
    return STATUS_PERMISSIONS[status] || STATUS_PERMISSIONS[APPLICATION_STATUS.DRAFT];
};

/**
 * Get display properties for a status (uses mapped status for UI)
 * @param {string} status - The application status
 * @returns {Object} - Display properties object
 */
export const getStatusDisplay = (status) => {
    const displayStatus = getDisplayStatus(status);
    let display = STATUS_DISPLAY[displayStatus] || STATUS_DISPLAY[APPLICATION_STATUS.DRAFT];
    // Ensure message is also mapped for temporary logic
    if (status === APPLICATION_STATUS.REVIEWED || status === APPLICATION_STATUS.REJECTED) {
        display = {
            ...display,
            message: 'Application submitted successfully'
        };
    }
    return display;
};

/**
 * Check if a status transition is valid
 * @param {string} currentStatus - Current application status
 * @param {string} newStatus - Target status
 * @returns {boolean} - Whether the transition is valid
 */
export const isValidStatusTransition = (currentStatus, newStatus) => {
    const allowedTransitions = STATUS_FLOW[currentStatus] || [];
    return allowedTransitions.includes(newStatus);
};

/**
 * Determine the next status when submitting an application
 * @param {Object} applicationData - Application data
 * @param {boolean} hasSignedForm - Whether signed form is already uploaded
 * @returns {string} - Next status
 */
export const getSubmissionStatus = (applicationData, hasSignedForm = false) => {
    const currentStatus = applicationData.status;
    
    // Don't automatically change RE_UPLOAD_SIGNED_FORM status to SUBMITTED
    // even if signed form exists - admin needs to manually approve re-uploaded form
    if (currentStatus === APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM) {
        return APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM;
    }
    
    // If signed form is already uploaded, set to Submitted
    if (hasSignedForm || applicationData.applicationSignedFormId) {
        return APPLICATION_STATUS.SUBMITTED;
    }
    
    // If no signed form, require it first
    return APPLICATION_STATUS.PENDING_SIGNED_FORM;
};

/**
 * Determine the status when saving as draft
 * @param {Object} applicationData - Application data
 * @returns {string} - Draft status
 */
export const getDraftStatus = (applicationData) => {
    // Always maintain Draft status when saving as draft
    // unless it's in Missing Information state (allow to stay in that state)
    if (applicationData.status === APPLICATION_STATUS.MISSING_INFORMATION) {
        return APPLICATION_STATUS.MISSING_INFORMATION;
    }
    return APPLICATION_STATUS.DRAFT;
};

/**
 * Check if application can be edited based on status
 * @param {string} status - Application status
 * @returns {boolean} - Whether application can be edited
 */
export const canEditApplication = (status) => {
    const permissions = getStatusPermissions(status);
    return permissions.canEdit;
};

/**
 * Check if application is in a final state
 * @param {string} status - Application status
 * @returns {boolean} - Whether application is in final state
 */
export const isFinalStatus = (status) => {
    return status === APPLICATION_STATUS.AWARDED || status === APPLICATION_STATUS.REJECTED;
};

/**
 * Get all possible next statuses for current status
 * @param {string} currentStatus - Current application status
 * @returns {Array} - Array of possible next statuses
 */
export const getPossibleNextStatuses = (currentStatus) => {
    return STATUS_FLOW[currentStatus] || [];
};

/**
 * Validate application data based on status requirements
 * @param {Object} applicationData - Application data
 * @param {string} targetStatus - Target status to validate against
 * @returns {Object} - Validation result with isValid and errors
 */
export const validateApplicationForStatus = (applicationData, targetStatus) => {
    const errors = [];
    let isValid = true;

    switch (targetStatus) {
        case APPLICATION_STATUS.SUBMITTED:
            // Check if signed form is uploaded
            if (!applicationData.applicationSignedFormId) {
                errors.push('Signed application form must be uploaded before submission');
                isValid = false;
            }
            break;

        case APPLICATION_STATUS.PENDING_SIGNED_FORM:
            // Basic form validation should be done before this status
            // This is handled by the form validation itself
            break;

        default:
            // For other statuses, no additional validation needed from user side
            break;
    }

    return { isValid, errors };
};

/**
 * Get status-specific user messages (uses mapped status for UI)
 * @param {string} status - Application status
 * @param {Object} applicationData - Application data for context
 * @returns {Object} - Message object with type and text
 */
export const getStatusMessage = (status, applicationData = {}) => {
    const displayStatus = getDisplayStatus(status);
    let display = getStatusDisplay(status);
    let additionalInfo = '';

    // Ensure message is also mapped for temporary logic
    if (status === APPLICATION_STATUS.REVIEWED || status === APPLICATION_STATUS.REJECTED) {
        display = {
            ...display,
            message: 'Application submitted successfully'
        };
    }

    switch (displayStatus) {
        case APPLICATION_STATUS.PENDING_SIGNED_FORM:
            additionalInfo = ' Download the PDF, sign it, and upload the signed version.';
            break;
        case APPLICATION_STATUS.RE_UPLOAD_SIGNED_FORM:
            additionalInfo = ' There was an issue with your previously uploaded form. Please download a fresh copy, sign it, and upload again.';
            break;
        case APPLICATION_STATUS.MISSING_INFORMATION:
            additionalInfo = ' Please review the feedback and update the required information.';
            break;
        case APPLICATION_STATUS.AWARDED:
            if (applicationData.aidGranted) {
                additionalInfo = ` Aid Amount: ₹${parseFloat(applicationData.aidGranted).toLocaleString('en-IN')}`;
            }
            break;
    }

    return {
        type: displayStatus === APPLICATION_STATUS.AWARDED ? 'success' : 
              displayStatus === APPLICATION_STATUS.REJECTED ? 'danger' :
              displayStatus === APPLICATION_STATUS.MISSING_INFORMATION ? 'warning' : 'info',
        text: display.message + additionalInfo,
        icon: display.icon
    };
};

/**
 * Check if user can perform specific action based on status
 * @param {string} status - Application status
 * @param {string} action - Action to check (edit, submit, upload, etc.)
 * @returns {boolean} - Whether action is allowed
 */
export const canPerformAction = (status, action) => {
    const permissions = getStatusPermissions(status);
    
    switch (action) {
        case 'edit':
            return permissions.canEdit;
        case 'submit':
            return permissions.canSubmit;
        case 'saveDraft':
            return permissions.canSaveDraft;
        case 'downloadPDF':
            return permissions.canDownloadPDF;
        case 'uploadSignedForm':
            return permissions.canUploadSignedForm;
        case 'downloadSignedForm':
            return permissions.canDownloadSignedForm;
        case 'view':
            return permissions.canView;
        default:
            return false;
    }
};

/**
 * Validate and correct application status based on signed form presence
 * @param {Object} applicationData - Application data
 * @returns {Object} - Validation result with corrected status if needed
 */
export const validateAndCorrectApplicationStatus = (applicationData) => {
    const currentStatus = applicationData.status;
    const hasSignedForm = !!(applicationData.applicationSignedFormId);
    
    console.log(`Status validation for application:`, {
        id: applicationData.id,
        currentStatus,
        hasSignedForm,
        applicationSignedFormId: applicationData.applicationSignedFormId
    });
    
    // Check if application is marked as "Submitted" but has no signed form
    if (currentStatus === APPLICATION_STATUS.SUBMITTED && !hasSignedForm) {
        console.log(`Status correction needed: Submitted -> Pending Signed Form`);
        return {
            needsCorrection: true,
            correctedStatus: APPLICATION_STATUS.PENDING_SIGNED_FORM,
            reason: 'Application marked as Submitted but no signed form found',
            originalStatus: currentStatus
        };
    }
    
    // Check if application is in PENDING_SIGNED_FORM but already has signed form
    if (currentStatus === APPLICATION_STATUS.PENDING_SIGNED_FORM && hasSignedForm) {
        console.log(`Status correction needed: Pending Signed Form -> Submitted`);
        return {
            needsCorrection: true,
            correctedStatus: APPLICATION_STATUS.SUBMITTED,
            reason: 'Application in Pending Signed Form status but signed form already uploaded',
            originalStatus: currentStatus
        };
    }
    
    // Do NOT auto-correct RE_UPLOAD_SIGNED_FORM status even if signed form exists
    // Admin needs to manually approve the re-uploaded form
    
    console.log(`No status correction needed for application`);
    return {
        needsCorrection: false,
        correctedStatus: currentStatus,
        reason: 'Status is consistent with signed form presence',
        originalStatus: currentStatus
    };
};

/**
 * Edge case validations
 */

/**
 * Check for concurrent status changes
 * @param {Object} currentApplicationData - Current application data from UI
 * @param {Object} serverApplicationData - Latest application data from server
 * @returns {Object} - Validation result
 */
export const validateConcurrentChanges = (currentApplicationData, serverApplicationData) => {
    if (currentApplicationData.status !== serverApplicationData.status) {
        return {
            hasConflict: true,
            message: `Application status has been changed to "${serverApplicationData.status}" by another user or admin. Please refresh and try again.`,
            serverStatus: serverApplicationData.status
        };
    }
    return { hasConflict: false };
};

/**
 * Validate file upload requirements
 * @param {File} file - File to validate
 * @param {string} fileType - Expected file type
 * @returns {Object} - Validation result
 */
export const validateFileUpload = (file, fileType = 'pdf') => {
    const errors = [];
    let isValid = true;

    if (!file) {
        errors.push('No file selected');
        isValid = false;
        return { isValid, errors };
    }

    // File type validation
    if (fileType === 'pdf' && file.type !== 'application/pdf') {
        errors.push('Only PDF files are allowed');
        isValid = false;
    }

    // File size validation (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        errors.push('File size must be less than 10MB');
        isValid = false;
    }

    // File name validation
    if (file.name.length > 255) {
        errors.push('File name is too long');
        isValid = false;
    }

    return { isValid, errors };
};

/**
 * Get status-based error messages for user actions
 * @param {string} status - Current application status
 * @param {string} attemptedAction - Action user tried to perform
 * @returns {string} - Error message
 */
export const getActionErrorMessage = (status, attemptedAction) => {
    const statusName = status.toLowerCase();
    
    switch (attemptedAction) {
        case 'edit':
            return `Cannot edit application. Current status: ${status}. Applications can only be edited when in Draft or Missing Information status.`;
        case 'submit':
            return `Cannot submit application. Current status: ${status}. Please check the application requirements.`;
        case 'uploadSignedForm':
            return `Cannot upload signed form. Current status: ${status}. Signed forms can only be uploaded when in Pending Signed Form or Re-Upload Signed Form status.`;
        default:
            return `Action not allowed for current application status: ${status}`;
    }
};
